import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import useInitPurchase from '@hooks/useInitPurchase';
import { wrapper } from 'store';
import FormLayout from '@/containers/formLayout';
import ChooseOperation from '@/containers/chooseOperationArr';
import ClientPersonalData from '@/containers/clientPersonalData';
import ClientPayment from '@/containers/clientPayment';
import FormMobileLineFake from '@/components/fakes/formPagesFake/formMobileLineClientFake';
import Title, { TITLE_SIZE } from '@/components/title';
import { SEGMENT, BUBBLE_TEXTS, LINE_TYPE } from '@/constants/constant';
import { capitalizeWords, sendTaggingInfo } from '@/utils/common';
import {
  URL_SEO,
  PATH_NAME,
  FORM_ID,
  TRANSACTION_TYPE,
} from '@/constants/pages/mobileLines';
import { actions as formActions } from '@/reducers/form-mobile-line-reducer';
import { actions as addressActions } from '@/reducers/address-reducer';
import { getStaticProps as getStaticPropsMethod } from '@/bit/pages/pages.create-pages';
import getDeviceKindDependsOnViewPort from '@gcloud-npm/utils.common.get-device-kind-depends-on-viewport';
import { PERSONAL_DATA } from '@/constants/personalData';
import { MOBILE_LINE_ACTIONS, OPERATION } from '@/constants/operation';
import {
  clientCalculateShippingText,
  getDeliveryText,
} from '@/utils/orionTextHelper/lm';
import Shipping from '@/containers/shipping';
import { actions as retargetingActions } from '@/reducers/retargeting-reducer';

const MobileLineClientForm = ({ segment = SEGMENT.particulares }) => {
  const dispatch = useDispatch();
  const formMobileLine = useSelector((state) => state.formMobileLine);
  const { purchaseOptions } = formMobileLine;
  const { productVariabilities } = { ...purchaseOptions };
  const { lm } = { ...productVariabilities };
  const { shippingTypes } = { ...lm };

  const {
    initialShoppingCart,
    shoppingCart,
    loadingShoppingCart,
    checkout,
    lines,
    customerInfo,
    orionTexts,
  } = formMobileLine;

  const [showErrorBubble, setShowErrorBubble] = useState(false);
  const [formLoaded, setFormLoaded] = useState(false);
  const [calculatedShippingTexts, setCalculatedShippingTexts] = useState(false);
  const [isMigration, setIsMigration] = useState(false);
  const [deliveryText, setDeliveryText] = useState(null);

  const {
    unregister,
    getValues,
    handleSubmit,
    resetField,
    control,
    watch,
    setValue,
  } = useForm({
    mode: 'onTouched',
  });
  const operationFormData = useWatch({ control, name: OPERATION.ID });
  const { setSegment, initPurchase } = formActions;
  const { setZone } = addressActions;

  useInitPurchase({
    segment,
    initPurchase,
    setSegment,
    setZone,
  });

  const onSubmit = async (formData) => {
    setShowErrorBubble(false);
    const deviceKind = getDeviceKindDependsOnViewPort();
    dispatch(
      formActions.checkout({
        formData,
        deviceKind,
      })
    );
  };
  const onError = (formErrors) => {
    if (Object.keys(formErrors).length > 1) {
      setShowErrorBubble(true);
      const scrollElement = document.getElementById(FORM_ID.GP);
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'instant' });
      }
    } else if (Object.keys(formErrors).length === 1) {
      setShowErrorBubble(false);
      const scrollElement = document.getElementById(Object.keys(formErrors)[0]);
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'instant' });
      }
    }
  };
  useEffect(() => {
    if (Array.isArray(operationFormData)) {
      setCalculatedShippingTexts(
        clientCalculateShippingText({
          orionTexts,
          operationFormData,
          shippingTypes,
        })
      );

      const isMobileMigrationChosen = operationFormData?.some(
        (item) =>
          item.type === LINE_TYPE.MOBILE_LINE &&
          item[OPERATION.ACTION_CHOICER.ID]?.value ===
            MOBILE_LINE_ACTIONS.MIGRATION
      );

      setIsMigration(isMobileMigrationChosen);
    }
    setDeliveryText(getDeliveryText(orionTexts));
  }, [operationFormData]);

  useEffect(() => {
    if (
      lines &&
      (!Array.isArray(operationFormData) ||
        (Array.isArray(operationFormData) && operationFormData.length === 0))
    ) {
      setValue(OPERATION.ID, Array.isArray(lines) ? lines : []);

      setFormLoaded(true);
    }
  }, [lines]);

  useEffect(() => {
    if (
      initialShoppingCart !== null &&
      window.grecaptcha &&
      window.grecaptcha.enterprise
    ) {
      window.grecaptcha.enterprise.ready(() => {
        dispatch(
          retargetingActions.sendRetargeting({
            ...initialShoppingCart,
            campaignId: 'ret-lm-cart',
          })
        );
        const { telcoProductName, totalRecurringPrice } = {
          ...initialShoppingCart,
        };
        const { finalPrice } = {
          ...totalRecurringPrice,
        };
        sendTaggingInfo(
          {
            product: telcoProductName,
            price: finalPrice,
            funnelPurchaseMode: '',
            userType: '',
            funnelStepName: 'formulario',
            transactionType: TRANSACTION_TYPE,
            funnelFlowName: 'movistar-layer',
          },
          '',
          'movistar_layer_google_ads'
        );
      });
    }
  }, [initialShoppingCart, dispatch]);

  return (
    <>
      <Head>
        <script
          src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_LAYER_RECAPTCHA_ID}`}
          async
        />
      </Head>
      <FormLayout
        pageId={FORM_ID.GP}
        onSubmit={handleSubmit(onSubmit, onError)}
        control={control}
        showErrorBubble={showErrorBubble}
        textBubble={BUBBLE_TEXTS.BUBBLE_ERROR_TEXT}
        loaded={formLoaded}
        shoppingCart={shoppingCart}
        loadingShoppingCart={loadingShoppingCart}
        loadingButtonShoppingCart={checkout}
        formFake={<FormMobileLineFake />}
      >
        <Title
          text={`Hola${
            customerInfo?.name ? ` ${capitalizeWords(customerInfo?.name)}` : ''
          }, completa tu pedido`}
          size={TITLE_SIZE.TITLE5}
        />
        <ChooseOperation
          title={Array.isArray(lines) && lines.length > 1 ? '' : 'Línea móvil'}
          control={control}
          getValues={getValues}
          lines={lines}
          watch={watch}
          setValue={setValue}
          nextStep={calculatedShippingTexts?.nextStep || null}
        />
        <Shipping
          control={control}
          getValues={getValues}
          unregister={unregister}
          resetField={resetField}
          shippingTitle={calculatedShippingTexts?.title}
          shippingText={calculatedShippingTexts?.description}
          shippingType={calculatedShippingTexts?.shippingType}
          defaultAddress={customerInfo?.shippingAddress?.addressSelected}
          deliveryText={deliveryText}
          setValue={setValue}
          client
        />
        {!isMigration && customerInfo && customerInfo.bankAccount && (
          <ClientPayment
            control={control}
            bankAccount={customerInfo.bankAccount?.numberBankAccount}
          />
        )}

        <ClientPersonalData
          title={isMigration ? 'Datos de contacto y pago' : 'Datos de contacto'}
          contactEmail={customerInfo?.contactMedia?.contactEmail}
          contactPhone={customerInfo?.contactMedia?.contactPhone}
          payment={isMigration ? 'En tu factura Movistar.' : ''}
          getValues={getValues}
          control={control}
          setValue={setValue}
        />
      </FormLayout>
    </>
  );
};
export default MobileLineClientForm;

export const getStaticProps = wrapper.getStaticProps(async (data) => {
  return getStaticPropsMethod(data, '', 2, {
    pathname: PATH_NAME.GP,
    orionAlias: '',
    seoLocation: URL_SEO,
    headerLocation: '/',
    layoutInfo: '',
  });
});
